import React, { useState, useEffect, useRef } from "react";

// ── Data ────────────────────────────────────────────────────────────────────

const WORKER_URL = "https://reflex-backend.alfaoir1.workers.dev";

const sections = [
  { label: "CRM & Lead Mgmt",       qs: [1, 2] },
  { label: "Sales Pipeline",         qs: [3, 4, 5] },
  { label: "Cold Outreach",          qs: [6, 7, 8] },
  { label: "AI & Automation",        qs: [9, 10] },
  { label: "Website & Conversion",   qs: [11, 12, 13] },
  { label: "Mobile & Field Ops",     qs: [14, 15] },
  { label: "Operations",             qs: [16, 17, 18] },
  { label: "Marketing & Brand",      qs: [19, 20] },
  { label: "Commercial Expansion",   qs: [21, 22] },
  { label: "Revenue & Investment",   qs: [23, 24, 25] },
];

const engineData = {
  eco: {
    name: "ECONOMY ENGINE",
    tagline: "4-Cylinder Starter — Efficient but Capacity-Limited",
    desc: "Your business is running lean and steady. The fundamentals are in place, but manual processes and a residential-first focus are creating an invisible ceiling on growth. Without CRM automation, commercial outreach, or AI systems, every additional client adds operational weight rather than momentum. The path forward requires systematizing before scaling.",
    cls: "eco",
    color: "#22c55e",
    gaps: ["CRM & Pipeline System", "Lead Nurture Automation", "Cold Outreach Engine", "Commercial Landing Pages", "AI Lead Qualification", "Proposal Automation"],
    tier: "🟢",
  },
  v6: {
    name: "V6 PERFORMANCE ENGINE",
    tagline: "Sport Spec — Power Available, Systems Need Tuning",
    desc: "You've built a real business with momentum. Some systems exist, commercial intent is present, and growth is happening — but inconsistency in follow-up, outreach, and automation is leaving significant revenue on the table. A targeted systems upgrade would unlock predictable commercial contract flow and remove the friction slowing your close rate.",
    cls: "v6",
    color: "#3b82f6",
    gaps: ["Structured Outreach Sequences", "Commercial Landing Pages", "Appointment Setting System", "AI Lead Handler", "Mobile Field App", "Automated Proposal Engine"],
    tier: "🔵",
  },
  v12: {
    name: "V12 TURBO BEAST",
    tagline: "Hypercar Spec — Enterprise Scaling Machine",
    desc: "You are wired for enterprise. The vision, investment readiness, and commercial ambition are all aligned. What separates good from dominant at this level is the full-stack: AI systems, outreach automation, custom apps, and a commercial contract engine firing on all cylinders. You're not optimising a small business — you're building a scalable infrastructure.",
    cls: "v12",
    color: "#ef4444",
    gaps: ["Custom Mobile App Build", "AI Voice + Chat System", "Full CRM Enterprise Stack", "Commercial Lead Gen Engine", "Appointment Setter Team", "Advanced Analytics Dashboard"],
    tier: "🔴",
  },
};

const tierKeys = ["eco", "v6", "v12"];

const questions = [
  // Section 1
  { id: 1,  section: "01 — CRM & Lead Management",    text: "What CRM system are you currently using to manage leads and customers?", options: ["Spreadsheets, paper notes, or no formal system", "Basic CRM (Jobber, HousecallPro, or similar entry-level)", "Full pipeline CRM with automations, tags, and lead scoring"] },
  { id: 2,  section: null,                              text: "What happens to leads that don't convert within 48 hours?",              options: ["They usually get lost or forgotten", "Manual follow-up, depends on who remembers", "Automated multi-step nurture sequence (email + SMS)"] },
  // Section 2
  { id: 3,  section: "02 — Sales Pipeline & Closing",  text: "How would you describe your current commercial sales process?",         options: ["No defined sales process — deals happen informally", "Basic process exists but it's inconsistent and manual", "Structured pipeline: lead → qualify → proposal → close → onboard"] },
  { id: 4,  section: null,                              text: "How are commercial proposals or bids currently being handled?",         options: ["Verbal agreements or handwritten estimates", "Email-based proposals created manually each time", "Automated proposal system with templates, e-sign, and tracking"] },
  { id: 5,  section: null,                              text: "Do you have a dedicated sales closer or appointment setter?",           options: ["Owner handles all sales personally", "Part-time or shared sales support in place", "Dedicated setter + closer team with defined KPIs"] },
  // Section 3
  { id: 6,  section: "03 — Cold Outreach Engine",      text: "What is your current strategy for reaching property managers and HOAs?", options: ["No structured outreach — relying on inbound or word-of-mouth", "Occasional outreach via personal networking or referrals", "Systematic cold email + LinkedIn + cold calling campaigns running"] },
  { id: 7,  section: null,                              text: "Do you run cold email campaigns targeting commercial clients?",          options: ["No — never tried cold email outreach", "Tried it but no consistent system in place", "Running automated sequences with A/B testing and reply handling"] },
  { id: 8,  section: null,                              text: "How are inbound commercial inquiries currently handled?",                options: ["Phone or email response whenever owner has time", "Basic form or contact page with 24–48hr response", "AI chat + instant qualifying flow + auto-booked discovery call"] },
  // Section 4
  { id: 9,  section: "04 — AI & Automation Layer",     text: "What level of automation currently exists in your lead handling?",     options: ["Fully manual — owner responds to every inquiry personally", "Auto-responder emails only (no smart qualification)", "AI qualifies, scores, and routes leads automatically"] },
  { id: 10, section: null,                              text: "Do you use AI chat or voice assistants on your website?",              options: ["No — just a basic contact form", "Basic live chat widget (no automation)", "AI chat + voice bot that handles Q&A, qualifies, and books"] },
  // Section 5
  { id: 11, section: "05 — Website & Conversion System", text: "How would you describe your current business website?",              options: ["Basic info page or no dedicated website", "Functional site with booking but limited commercial focus", "Full conversion machine: booking + CRM + portal + AI + commercial pages"] },
  { id: 12, section: null,                              text: "Do you have dedicated landing pages for HOAs and property managers?",   options: ["No — commercial clients land on the same generic page", "Basic commercial page exists but not optimized for conversion", "Targeted landing pages per segment with case studies + instant quote"] },
  { id: 13, section: null,                              text: "What happens after a visitor fills out your contact form?",            options: ["An email notification — owner manually follows up when available", "Auto-confirmation email + owner assigned to follow up", "Instant AI response + CRM entry + 7-step automated nurture sequence"] },
  // Section 6
  { id: 14, section: "06 — Mobile & Field Systems",    text: "How do your field technicians receive job assignments and updates?",   options: ["Phone calls, texts, or paper schedules", "Basic app or shared calendar (no real-time updates)", "Custom mobile app with GPS routing, photo upload, and status updates"] },
  { id: 15, section: null,                              text: "How is route optimization currently handled for field teams?",         options: ["No optimization — techs plan their own routes manually", "Basic scheduling software with manual routing input", "AI-optimized routing with real-time adjustments and fleet tracking"] },
  // Section 7
  { id: 16, section: "07 — Operations & Workflow Automation", text: "How is invoicing and payment collection currently handled?",    options: ["Manual invoices sent via text or email — cash/Venmo payments", "Software like QuickBooks or Jobber with some automation", "Fully automated billing, recurring charges, and payment reminders"] },
  { id: 17, section: null,                              text: "How are customer service inquiries and complaints managed?",           options: ["Owner handles everything personally via phone", "Shared inbox or basic ticketing system", "AI handles Tier 1 support; escalation workflows for complex issues"] },
  { id: 18, section: null,                              text: "Are your current systems capable of handling 2–3x your current volume?", options: ["No — everything would break above current volume", "Possibly — with some manual adjustments and new hires", "Yes — systems are built to scale, no bottleneck in sight"] },
  // Section 8
  { id: 19, section: "08 — Marketing & Brand Positioning", text: "How would you describe your current commercial brand authority?", options: ["Mostly known as a neighbourhood residential service", "Mixed presence — some commercial clients but no strong positioning", "Known brand in the commercial space with case studies + reviews"] },
  { id: 20, section: null,                              text: "Do you run paid advertising campaigns targeting commercial prospects?", options: ["No paid ads — organic or word-of-mouth only", "Basic Google/Facebook ads but not optimized for commercial", "Targeted paid campaigns with retargeting, lookalikes, and attribution"] },
  // Section 9
  { id: 21, section: "09 — Commercial Expansion Readiness", text: "What is the primary revenue model driving your growth strategy?", options: ["One-time residential jobs (no recurring focus)", "Residential recurring subscriptions are building", "Commercial multi-year contracts + residential recurring MRR"] },
  { id: 22, section: null,                              text: "What commercial expansion are you most actively pursuing right now?",  options: ["Not focused on commercial yet — residential first", "Apartment complexes and HOA contracts", "Full portfolio: HOAs, property managers, dog parks, enterprise deals"] },
  // Section 10
  { id: 23, section: "10 — Revenue Scaling & Investment Readiness", text: "What is your current monthly recurring revenue (MRR) range?", options: ["Under $10,000/month", "$10,000 – $50,000/month", "$50,000+/month"] },
  { id: 24, section: null,                              text: "What is your growth revenue target for the next 12 months?",           options: ["Maintain current revenue — stable growth only", "1.5x–2x current revenue (structured growth)", "3x–10x through commercial contracts + automation scale"] },
  { id: 25, section: null,                              text: "What monthly budget are you ready to invest in scaling systems?",      options: ["$500 – $1,500/month (starter automation)", "$1,500 – $5,000/month (growth systems)", "$5,000+/month (full enterprise stack)"] },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function useCountUp(target, duration = 1200, active = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    const start = Date.now();
    let raf;
    const step = () => {
      const t = Math.min(1, (Date.now() - start) / duration);
      const ease = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(target * ease));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, active]);
  return val;
}

// ── Engine SVGs ──────────────────────────────────────────────────────────────

function EcoSVG({ color }) {
  return (
    <svg width="480" height="260" viewBox="0 0 480 260" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="glow-eco">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <linearGradient id="metalEco" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#374151" />
          <stop offset="100%" stopColor="#1f2937" />
        </linearGradient>
      </defs>
      <rect x="140" y="100" width="200" height="110" rx="6" fill="url(#metalEco)" stroke={color} strokeWidth="1.5" opacity="0.9" />
      {[165, 205, 245, 285].map((x, i) => (
        <g key={x}>
          <rect x={x} y="70" width="28" height="36" rx="3" fill="#1f2937" stroke={color} strokeWidth="1.5" />
          <rect x={x + 6} y="60" width="16" height="14" rx="2" fill="#111827" stroke={color} strokeWidth="1" />
          <circle cx={x + 14} cy="53" r="4" fill={color} opacity="0.7" filter="url(#glow-eco)">
            <animate attributeName="opacity" values="0.7;1;0.7" dur={`${1.2 + i * 0.05}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}
      <rect x="150" y="95" width="180" height="12" rx="3" fill="#374151" stroke={color} strokeWidth="1" opacity="0.8" />
      {[165, 205, 245, 285].map(x => (
        <rect key={x} x={x + 6} y="208" width="14" height="28" rx="3" fill="#111827" stroke={color} strokeWidth="1" opacity="0.7" />
      ))}
      <text x="240" y="258" textAnchor="middle" fontFamily="'Space Mono',monospace" fontSize="11" fill={color} letterSpacing="3" opacity="0.9">EFFICIENT STARTER ENGINE</text>
      <text x="240" y="148" textAnchor="middle" fontFamily="'Space Mono',monospace" fontSize="9" fill="#6b7280" letterSpacing="2">4-CYLINDER ECONOMY</text>
      {[155, 185, 215, 245, 275, 305].map(x => (
        <circle key={x} cx={x} cy="210" r="3" fill={color} opacity="0.4" />
      ))}
    </svg>
  );
}

function V6SVG({ color }) {
  return (
    <svg width="520" height="280" viewBox="0 0 520 280" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="glow-v6">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <linearGradient id="metalV6" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1e3a5f" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>
      </defs>
      {[170, 200, 230].map((x, i) => (
        <g key={`L${x}`}>
          <rect x={x - 14} y={130 - i * 8} width="26" height="50" rx="3" fill="url(#metalV6)" stroke={color} strokeWidth="1.5" transform={`rotate(-15 ${x} 155)`} opacity="0.9" />
          <circle cx={x - i * 4} cy={110 - i * 12} r="5" fill={color} filter="url(#glow-v6)" opacity="0.8">
            <animate attributeName="opacity" values="0.8;1;0.8" dur={`${0.9 + i * 0.15}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}
      {[290, 320, 350].map((x, i) => (
        <g key={`R${x}`}>
          <rect x={x - 12} y={130 - i * 8} width="26" height="50" rx="3" fill="url(#metalV6)" stroke={color} strokeWidth="1.5" transform={`rotate(15 ${x} 155)`} opacity="0.9" />
          <circle cx={x + i * 4} cy={110 - i * 12} r="5" fill={color} filter="url(#glow-v6)" opacity="0.8">
            <animate attributeName="opacity" values="0.8;1;0.8" dur={`${1.1 + i * 0.15}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}
      <rect x="195" y="150" width="130" height="80" rx="5" fill="url(#metalV6)" stroke={color} strokeWidth="2" />
      <ellipse cx="260" cy="148" rx="60" ry="16" fill="#1e3a5f" stroke={color} strokeWidth="1.5" opacity="0.9" />
      <rect x="240" y="130" width="40" height="20" rx="4" fill="#0f172a" stroke={color} strokeWidth="1.5" />
      <rect x="235" y="115" width="50" height="20" rx="3" fill={color} opacity="0.15" stroke={color} strokeWidth="1" />
      <text x="260" y="128" textAnchor="middle" fontFamily="'Space Mono',monospace" fontSize="7" fill={color} letterSpacing="1">V6 INTAKE</text>
      <text x="260" y="272" textAnchor="middle" fontFamily="'Space Mono',monospace" fontSize="11" fill={color} letterSpacing="3">PERFORMANCE GROWTH ENGINE</text>
      <text x="260" y="218" textAnchor="middle" fontFamily="'Space Mono',monospace" fontSize="9" fill="#4b6fa8" letterSpacing="2">V6 SPORT SPEC</text>
    </svg>
  );
}

function V12SVG({ color }) {
  return (
    <svg width="560" height="300" viewBox="0 0 560 300" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="glow-v12">
          <feGaussianBlur stdDeviation="5" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="halo">
          <feGaussianBlur stdDeviation="8" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <linearGradient id="metalV12" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#3d1010" />
          <stop offset="100%" stopColor="#0a0a0a" />
        </linearGradient>
        <linearGradient id="turboGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={color} stopOpacity="0.8" />
          <stop offset="100%" stopColor="#f97316" stopOpacity="0.8" />
        </linearGradient>
      </defs>
      <ellipse cx="280" cy="180" rx="180" ry="60" fill={color} opacity="0.04" filter="url(#halo)" />
      {[130, 158, 186, 214, 242, 270].map((x, i) => (
        <g key={`L${x}`}>
          <rect x={x - 10} y={95 - i * 2} width="24" height="60" rx="3" fill="url(#metalV12)" stroke={color} strokeWidth="1.5" transform={`rotate(-18 ${x} 155)`} opacity="0.9" />
          <circle cx={x - 14 - i * 2} cy={78 - i * 9} r="5" fill={color} filter="url(#glow-v12)">
            <animate attributeName="r" values="5;6.5;5" dur={`${0.7 + i * 0.12}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}
      {[290, 318, 346, 374, 402, 430].map((x, i) => (
        <g key={`R${x}`}>
          <rect x={x - 10} y={95 - i * 2} width="24" height="60" rx="3" fill="url(#metalV12)" stroke={color} strokeWidth="1.5" transform={`rotate(18 ${x} 155)`} opacity="0.9" />
          <circle cx={x + 14 + i * 2} cy={78 - i * 9} r="5" fill={color} filter="url(#glow-v12)">
            <animate attributeName="r" values="5;6.5;5" dur={`${0.85 + i * 0.12}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}
      <rect x="180" y="145" width="200" height="90" rx="6" fill="url(#metalV12)" stroke={color} strokeWidth="2.5" />
      <ellipse cx="195" cy="240" rx="28" ry="18" fill="#1a0505" stroke={color} strokeWidth="2" filter="url(#glow-v12)" />
      <ellipse cx="195" cy="240" rx="18" ry="12" fill={color} opacity="0.2" />
      <text x="195" y="244" textAnchor="middle" fontFamily="'Space Mono',monospace" fontSize="7" fill={color}>TURBO</text>
      <ellipse cx="365" cy="240" rx="28" ry="18" fill="#1a0505" stroke={color} strokeWidth="2" filter="url(#glow-v12)" />
      <ellipse cx="365" cy="240" rx="18" ry="12" fill={color} opacity="0.2" />
      <text x="365" y="244" textAnchor="middle" fontFamily="'Space Mono',monospace" fontSize="7" fill={color}>TURBO</text>
      <path d="M 223 235 Q 260 225 280 190 Q 300 225 337 235" fill="none" stroke="url(#turboGrad)" strokeWidth="3" strokeLinecap="round" />
      <rect x="220" y="138" width="120" height="14" rx="4" fill={color} opacity="0.25" stroke={color} strokeWidth="1.5" />
      <rect x="250" y="120" width="60" height="22" rx="4" fill="#1a0505" stroke={color} strokeWidth="1.5" />
      <text x="280" y="134" textAnchor="middle" fontFamily="'Space Mono',monospace" fontSize="7" fill={color} letterSpacing="1">V12 INTAKE</text>
      {[220, 240, 260, 280, 300, 320, 340].map((x, i) => (
        <line key={x} x1={x} y1="260" x2={x + 5} y2="278" stroke={color} strokeWidth="1" opacity="0.15">
          <animate attributeName="opacity" values="0.15;0.35;0.15" dur={`${1 + i * 0.1}s`} repeatCount="indefinite" />
        </line>
      ))}
      <text x="280" y="295" textAnchor="middle" fontFamily="'Space Mono',monospace" fontSize="12" fill={color} letterSpacing="3" filter="url(#glow-v12)">ENTERPRISE BEAST ENGINE</text>
      <text x="280" y="194" textAnchor="middle" fontFamily="'Space Mono',monospace" fontSize="9" fill="#7f2020" letterSpacing="2">V12 TWIN-TURBO HYPERCAR SPEC</text>
    </svg>
  );
}

// ── Score Arc ────────────────────────────────────────────────────────────────

function ScoreRing({ score, color, active }) {
  const displayed = useCountUp(score, 1200, active);
  const circumference = 502;
  const offset = active ? circumference * (1 - score / 75) : circumference;
  return (
    <div style={{ position: "relative", width: 180, height: 180, margin: "40px auto" }}>
      <svg width="180" height="180" viewBox="0 0 180 180" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="90" cy="90" r="80" fill="none" stroke="#1e2535" strokeWidth="8" />
        <circle
          cx="90" cy="90" r="80"
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 1.5s cubic-bezier(0.4,0,0.2,1)" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 52, lineHeight: 1, letterSpacing: 2, color }}>{displayed}</span>
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#5a6280", marginTop: 4 }}>OUT OF 75</span>
      </div>
    </div>
  );
}

// ── Bar Row ───────────────────────────────────────────────────────────────────

function BarRow({ label, score, max, color, delay }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth((score / max) * 100), delay);
    return () => clearTimeout(t);
  }, [score, max, delay]);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 40px", alignItems: "center", gap: 16 }}>
      <span style={{ fontSize: 12, color: "#5a6280", textAlign: "right" }}>{label}</span>
      <div style={{ height: 6, background: "#1e2535", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${width}%`, background: color, borderRadius: 3, transition: "width 1s ease" }} />
      </div>
      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#e8eaf2" }}>{score}/{max}</span>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function DiagnosticForm() {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [userInfo, setUserInfo] = useState({ name: "", email: "", phone: "", website: "" });
  const [infoSubmitted, setInfoSubmitted] = useState(false);
  const [infoLoading, setInfoLoading] = useState(false);
  const resultsRef = useRef(null);

  const answered = Object.keys(answers).length;
  const progress = (answered / 25) * 100;
  const allDone = answered === 25;

  const totalScore = Object.values(answers).reduce((s, v) => s + v, 0);
  const engine =
    totalScore <= 40 ? engineData.eco :
    totalScore <= 60 ? engineData.v6  :
    engineData.v12;

  const sectionScores = sections.map(sec => ({
    label: sec.label,
    score: sec.qs.reduce((s, q) => s + (answers[q] || 0), 0),
    max: sec.qs.length * 3,
  }));

  function handleSelect(qId, val) {
    setAnswers(prev => ({ ...prev, [qId]: val }));
  }

  async function handleSubmit() {
    setSubmitted(true);
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);

    const engineType =
      totalScore <= 40 ? "eco" :
      totalScore <= 60 ? "v6" :
      "v12";

    const payload = {
      answers,
      totalScore,
      engineType,
      engineName: engineData[engineType].name,
      submittedAt: new Date().toISOString(),
    };

    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const res = await fetch(`${WORKER_URL}/diagnostic-form`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.success) { console.log("✅ Diagnostic saved"); return; }
        else console.warn(`Attempt ${attempt} failed:`, data.error);
      } catch (error) {
        console.warn(`Attempt ${attempt} network error:`, error.message);
      }
      if (attempt < 3) await new Promise((r) => setTimeout(r, attempt * 500));
    }
    console.error("❌ All retry attempts failed");
  }

  async function handleInfoSubmit() {
    if (!userInfo.name || !userInfo.email) return;
    setInfoLoading(true);
    const engineType = totalScore <= 40 ? "eco" : totalScore <= 60 ? "v6" : "v12";
    const payload = {
      ...userInfo,
      totalScore,
      engineType,
      engineName: engineData[engineType].name,
      submittedAt: new Date().toISOString(),
    };
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const res = await fetch(`${WORKER_URL}/lead-capture`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.success) { setInfoSubmitted(true); break; }
      } catch (e) {
        console.warn(`Attempt ${attempt} failed`, e.message);
      }
      if (attempt < 3) await new Promise(r => setTimeout(r, attempt * 500));
    }
    setInfoLoading(false);
  }

  function handleRestart() {
    setAnswers({});
    setSubmitted(false);
    setUserInfo({ name: "", email: "", phone: "", website: "" });
    setInfoSubmitted(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const styles = {
    root: {
      background: "#080a0f",
      color: "#e8eaf2",
      fontFamily: "'DM Sans', sans-serif",
      minHeight: "100vh",
      overflowX: "hidden",
    },
    gridBg: {
      position: "fixed",
      inset: 0,
      backgroundImage: "linear-gradient(rgba(59,130,246,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.03) 1px, transparent 1px)",
      backgroundSize: "60px 60px",
      pointerEvents: "none",
      zIndex: 0,
    },
    container: {
      position: "relative",
      zIndex: 1,
      maxWidth: 860,
      margin: "0 auto",
      padding: "0 24px 80px",
    },
    header: { padding: "60px 0 50px", textAlign: "center" },
    badge: {
      display: "inline-flex", alignItems: "center", gap: 8,
      background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)",
      color: "#f59e0b", fontFamily: "'Space Mono', monospace", fontSize: 10,
      letterSpacing: 3, textTransform: "uppercase", padding: "6px 16px",
      borderRadius: 2, marginBottom: 28,
    },
    badgeDot: { width: 6, height: 6, background: "#f59e0b", borderRadius: "50%" },
    h1: {
      fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(48px,8vw,80px)",
      letterSpacing: 4, lineHeight: 0.95, color: "#fff", marginBottom: 20,
    },
    h1Span: {
      background: "linear-gradient(135deg,#3b82f6,#8b5cf6)",
      WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
    },
    headerSub: { fontSize: 16, color: "#5a6280", fontWeight: 300, maxWidth: 520, margin: "0 auto 36px", lineHeight: 1.7 },
    enginesRow: { display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" },
    chip: (c) => ({
      display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 2,
      fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: 1, border: "1px solid",
      ...(c === "eco" ? { borderColor: "rgba(34,197,94,0.3)", color: "#22c55e", background: "rgba(34,197,94,0.05)" } :
          c === "v6"  ? { borderColor: "rgba(59,130,246,0.3)", color: "#3b82f6", background: "rgba(59,130,246,0.05)" } :
                        { borderColor: "rgba(239,68,68,0.3)",  color: "#ef4444", background: "rgba(239,68,68,0.05)" }),
    }),
    progressWrapper: {
      position: "sticky", top: 0, zIndex: 100,
      background: "rgba(8,10,15,0.9)", backdropFilter: "blur(12px)",
      borderBottom: "1px solid #1e2535", padding: "14px 0", margin: "0 -24px 48px",
      paddingLeft: 24, paddingRight: 24,
    },
    progressMeta: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
    progressLabel: { fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#5a6280", letterSpacing: 2, textTransform: "uppercase" },
    progressCount: { fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#f59e0b" },
    progressTrack: { height: 3, background: "#1e2535", borderRadius: 2, overflow: "hidden" },
    progressFill: { height: "100%", background: "linear-gradient(90deg,#3b82f6,#8b5cf6,#ef4444)", borderRadius: 2, transition: "width 0.5s cubic-bezier(0.4,0,0.2,1)" },
    sectionLabel: {
      fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 4, color: "#f59e0b",
      textTransform: "uppercase", marginBottom: 24, paddingBottom: 12, borderBottom: "1px solid #1e2535",
      display: "flex", alignItems: "center", gap: 10,
    },
    sectionLine: { width: 20, height: 1, background: "#f59e0b", opacity: 0.5 },
    divider: { border: "none", borderTop: "1px solid #1e2535", margin: "40px 0" },
    questionBlock: { marginBottom: 36 },
    qHeader: { display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 16 },
    qNum: {
      fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#5a6280",
      background: "#131720", border: "1px solid #1e2535", padding: "4px 8px",
      minWidth: 40, textAlign: "center", flexShrink: 0, marginTop: 2,
    },
    qText: { fontSize: 15, fontWeight: 500, color: "#e8eaf2", lineHeight: 1.5 },
    optionsGrid: { display: "flex", flexDirection: "column", gap: 8, paddingLeft: 54 },
    optionBase: {
      display: "flex", alignItems: "center", gap: 14, padding: "14px 16px",
      background: "#0e1118", border: "1px solid #1e2535", borderRadius: 3,
      cursor: "pointer", transition: "all 0.2s ease", position: "relative", overflow: "hidden",
    },
    optionSelected: (tier) => ({
      eco: { borderColor: "rgba(34,197,94,0.4)",  background: "rgba(34,197,94,0.06)" },
      v6:  { borderColor: "rgba(59,130,246,0.4)", background: "rgba(59,130,246,0.06)" },
      v12: { borderColor: "rgba(239,68,68,0.4)",  background: "rgba(239,68,68,0.06)" },
    }[tier]),
    dot: (sel, tier) => ({
      width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
      border: `2px solid ${sel ? ({ eco: "#22c55e", v6: "#3b82f6", v12: "#ef4444" }[tier]) : "#252d42"}`,
      display: "flex", alignItems: "center", justifyContent: "center",
    }),
    dotInner: (sel, tier) => ({
      width: 6, height: 6, borderRadius: "50%",
      background: { eco: "#22c55e", v6: "#3b82f6", v12: "#ef4444" }[tier],
      transform: sel ? "scale(1)" : "scale(0)", transition: "transform 0.2s ease",
    }),
    optText: (sel, tier) => ({
      fontSize: 13.5, lineHeight: 1.4,
      color: sel ? ({ eco: "#22c55e", v6: "#3b82f6", v12: "#ef4444" }[tier]) : "#9aa3bf",
      transition: "color 0.2s",
    }),
    tierTag: (sel, tier) => ({
      marginLeft: "auto", fontFamily: "'Space Mono', monospace", fontSize: 9,
      letterSpacing: 1.5, flexShrink: 0,
      opacity: sel ? 0.7 : 0,
      color: { eco: "#22c55e", v6: "#3b82f6", v12: "#ef4444" }[tier],
    }),
    ctaWrapper: { textAlign: "center", marginTop: 56 },
    ctaBtn: (disabled) => ({
      display: "inline-flex", alignItems: "center", gap: 12,
      background: disabled ? "rgba(59,130,246,0.2)" : "linear-gradient(135deg,#3b82f6,#6d28d9)",
      color: "#fff", border: "none", padding: "18px 48px",
      fontFamily: "'Space Mono', monospace", fontSize: 13, fontWeight: 700,
      letterSpacing: 3, textTransform: "uppercase", cursor: disabled ? "not-allowed" : "pointer",
      borderRadius: 2, opacity: disabled ? 0.4 : 1,
      boxShadow: disabled ? "none" : "0 0 40px rgba(59,130,246,0.3)",
      transition: "all 0.3s ease",
    }),
    ctaNote: (done) => ({ marginTop: 14, fontSize: 12, color: done ? "#22c55e" : "#5a6280", fontFamily: "'Space Mono', monospace" }),
    resultHeader: { textAlign: "center", padding: "60px 0 48px" },
    resultEyebrow: { fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 4, color: "#f59e0b", textTransform: "uppercase", marginBottom: 20 },
    resultTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(36px,6vw,60px)", letterSpacing: 4, color: "#fff" },
    engineCard: (cls) => ({
      background: "#0e1118",
      border: `1px solid ${{ eco: "rgba(34,197,94,0.2)", v6: "rgba(59,130,246,0.2)", v12: "rgba(239,68,68,0.2)" }[cls]}`,
      borderRadius: 4, padding: 40, margin: "40px 0", position: "relative", overflow: "hidden",
    }),
    engineCardTop: (color) => ({
      position: "absolute", top: 0, left: 0, right: 0, height: 3,
      background: `linear-gradient(90deg,transparent,${color},transparent)`,
    }),
    engineNameDisplay: (cls) => ({
      fontFamily: "'Bebas Neue', sans-serif", fontSize: 52, letterSpacing: 6, lineHeight: 1, marginBottom: 8,
      color: { eco: "#22c55e", v6: "#3b82f6", v12: "#ef4444" }[cls],
    }),
    engineTagline: { fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 3, color: "#5a6280", textTransform: "uppercase", marginBottom: 24 },
    engineDesc: { fontSize: 15, color: "#8a94b0", lineHeight: 1.8, marginBottom: 32, maxWidth: 620 },
    gapsTitle: { fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 3, textTransform: "uppercase", marginBottom: 16, color: "#5a6280" },
    gapsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 10 },
    gapChip: (color) => ({
      display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
      background: "#131720", border: "1px solid #1e2535", borderRadius: 2,
      fontSize: 12, color: "#7a84a0",
    }),
    gapDot: (color) => ({ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, background: color }),
    breakdownSection: { margin: "48px 0" },
    breakdownTitle: {
      fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 4, color: "#f59e0b",
      textTransform: "uppercase", marginBottom: 24, paddingBottom: 12, borderBottom: "1px solid #1e2535",
    },
    breakdownBars: { display: "flex", flexDirection: "column", gap: 14 },
    engineViz: { margin: "48px 0", textAlign: "center" },
    engineVizTitle: { fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 4, color: "#5a6280", textTransform: "uppercase", marginBottom: 32 },
    restartBtn: {
      display: "inline-flex", alignItems: "center", gap: 8,
      background: "transparent", color: "#5a6280", border: "1px solid #1e2535",
      padding: "12px 28px", fontFamily: "'Space Mono', monospace", fontSize: 11,
      letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", borderRadius: 2,
      transition: "all 0.2s ease", marginTop: 32,
    },
  };

  // ── Form View ──────────────────────────────────────────────────────────────
  if (!submitted) {
    let lastSection = null;
    return (
      <div style={styles.root}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&family=Space+Mono:wght@400;700&display=swap');
          .diag-option:hover { transform: translateX(4px); border-color: #252d42 !important; }
          @keyframes diagPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(0.8)} }
          .diag-badge-dot { animation: diagPulse 2s infinite; }
          @keyframes diagFadeUp { to { opacity:1; transform:translateY(0); } }
          .diag-q { animation: diagFadeUp 0.5s ease forwards; opacity:0; transform:translateY(20px); }
        `}</style>
        <div style={styles.gridBg} />
        <div style={styles.container}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.badge}>
              <span className="diag-badge-dot" style={styles.badgeDot} />
              Business Intelligence Diagnostic System
            </div>
            <h1 style={styles.h1}>
              ENTERPRISE<br />
              <span style={styles.h1Span}>SCALING</span><br />
              DIAGNOSTIC
            </h1>
            <p style={styles.headerSub}>25 precision questions that classify your business scaling engine and expose every system gap standing between you and 10x growth.</p>
            <div style={styles.enginesRow}>
              <div style={styles.chip("eco")}>🟢 Economy Engine</div>
              <div style={styles.chip("v6")}>🔵 V6 Performance</div>
              <div style={styles.chip("v12")}>🔴 V12 Turbo Beast</div>
            </div>
          </div>

          {/* Progress */}
          <div style={styles.progressWrapper}>
            <div style={styles.progressMeta}>
              <span style={styles.progressLabel}>Diagnostic Progress</span>
              <span style={styles.progressCount}>{answered} / 25 ANSWERED</span>
            </div>
            <div style={styles.progressTrack}>
              <div style={{ ...styles.progressFill, width: `${progress}%` }} />
            </div>
          </div>

          {/* Questions */}
          {questions.map((q, idx) => {
            const showSection = q.section && q.section !== lastSection;
            if (q.section) lastSection = q.section;
            const selected = answers[q.id];
            return (
              <React.Fragment key={q.id}>
                {idx > 0 && showSection && <hr style={styles.divider} />}
                {showSection && (
                  <div style={styles.sectionLabel}>
                    <span style={styles.sectionLine} />
                    {q.section}
                  </div>
                )}
                <div className="diag-q" style={{ ...styles.questionBlock, animationDelay: `${0.05 + (idx % 5) * 0.05}s` }}>
                  <div style={styles.qHeader}>
                    <span style={styles.qNum}>{String(q.id).padStart(2, "0")}</span>
                    <span style={styles.qText}>{q.text}</span>
                  </div>
                  <div style={styles.optionsGrid}>
                    {q.options.map((opt, i) => {
                      const tier = tierKeys[i];
                      const isSel = selected === i + 1;
                      return (
                        <div
                          key={i}
                          className="diag-option"
                          style={{
                            ...styles.optionBase,
                            ...(isSel ? styles.optionSelected(tier) : {}),
                          }}
                          onClick={() => handleSelect(q.id, i + 1)}
                        >
                          <span style={styles.dot(isSel, tier)}>
                            <span style={styles.dotInner(isSel, tier)} />
                          </span>
                          <span style={styles.optText(isSel, tier)}>{opt}</span>
                          <span style={styles.tierTag(isSel, tier)}>{tier.toUpperCase()}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {/* CTA */}
          <div style={styles.ctaWrapper}>
            <button
              style={styles.ctaBtn(!allDone)}
              disabled={!allDone}
              onClick={handleSubmit}
            >
              <span>⚡</span>
              <span>ANALYZE MY ENGINE</span>
            </button>
            <p style={styles.ctaNote(allDone)}>
              {allDone
                ? "ALL 25 SYSTEMS ANALYZED — READY FOR CLASSIFICATION"
                : `${25 - answered} QUESTIONS REMAINING TO UNLOCK YOUR RESULT`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Results View ───────────────────────────────────────────────────────────
  return (
    <div style={styles.root} ref={resultsRef}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&family=Space+Mono:wght@400;700&display=swap');
        @keyframes diagFadeUp { to { opacity:1; transform:translateY(0); } }
        .diag-results { animation: diagFadeUp 0.8s ease forwards; opacity:0; transform:translateY(20px); }
        .diag-restart:hover { border-color:#252d42 !important; color:#e8eaf2 !important; }

        /* ── Lead Capture Inputs ── */
        .diag-capture-input {
          width: 100%;
          padding: 12px 14px;
          background: #131720;
          border: 1px solid #1e2535;
          border-radius: 3px;
          color: #e8eaf2;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          box-sizing: border-box;
        }
        .diag-capture-input::placeholder { color: #3a4260; }
        .diag-capture-input:focus {
          border-color: var(--eng-color, #3b82f6);
          box-shadow: 0 0 0 3px rgba(59,130,246,0.08);
        }
        .diag-capture-btn:hover:not(:disabled) { opacity: 0.88 !important; transform: translateY(-1px); }
        .diag-capture-btn { transition: all 0.2s ease !important; }
      `}</style>
      <div style={styles.gridBg} />
      <div style={{ ...styles.container }} className="diag-results">
        <div style={styles.resultHeader}>
          <div style={styles.resultEyebrow}>🔥 Your Business Engine Result</div>
          <div style={styles.resultTitle}>DIAGNOSTIC COMPLETE</div>
        </div>

        <ScoreRing score={totalScore} color={engine.color} active={true} />

        {/* ── Lead Capture Panel ── */}
        {!infoSubmitted ? (
          <div style={{
            background: "#0e1118",
            border: `1px solid ${engine.color}30`,
            borderRadius: 4,
            padding: "36px 40px",
            margin: "0 0 40px",
            position: "relative",
            overflow: "hidden",
            // pass engine color as CSS var for input focus ring
            ["--eng-color"]: engine.color,
          }}>
            {/* top accent line */}
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 2,
              background: `linear-gradient(90deg, transparent, ${engine.color}, transparent)`,
            }} />

            {/* panel label — matches PrivateAccessForm "Operator Identity" style */}
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              fontFamily: "'Space Mono', monospace", fontSize: 10,
              letterSpacing: "0.18em", textTransform: "uppercase",
              color: engine.color, opacity: 0.7,
              marginBottom: 6,
            }}>
              <span style={{ width: 16, height: 1, background: engine.color, opacity: 0.5 }} />
              Operator Identity
              <span style={{ flex: 1, height: 1, background: `${engine.color}22` }} />
            </div>

            <p style={{
              fontSize: 13, color: "#5a6280", marginBottom: 28, lineHeight: 1.65,
              fontFamily: "'Space Mono', monospace", letterSpacing: "0.02em",
            }}>
              Save your diagnostic &amp; receive your personalised growth plan.
            </p>

            {/* 2-col input grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {[
                { key: "name",    label: "Full Name *",     placeholder: "John Doe",             type: "text"  },
                { key: "email",   label: "Email Address *", placeholder: "you@email.com",        type: "email" },
                { key: "phone",   label: "Phone Number",    placeholder: "+1 (000) 000-0000",    type: "tel"   },
                { key: "website", label: "Website URL",     placeholder: "yoursite.com",         type: "text"  },
              ].map(({ key, label, placeholder, type }) => (
                <div key={key} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: 9, fontWeight: 700,
                    letterSpacing: "0.16em", textTransform: "uppercase",
                    color: `${engine.color}99`,
                  }}>
                    {label}
                  </label>
                  <input
                    className="diag-capture-input"
                    type={type}
                    placeholder={placeholder}
                    value={userInfo[key]}
                    onChange={e => setUserInfo(prev => ({ ...prev, [key]: e.target.value }))}
                    style={{ ["--eng-color"]: engine.color }}
                  />
                </div>
              ))}
            </div>

            {/* Submit button */}
            <div style={{ marginTop: 24 }}>
              <button
                className="diag-capture-btn"
                disabled={!userInfo.name || !userInfo.email || infoLoading}
                onClick={handleInfoSubmit}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 10,
                  background: (!userInfo.name || !userInfo.email || infoLoading)
                    ? "rgba(255,255,255,0.04)"
                    : `linear-gradient(135deg, ${engine.color}, #6d28d9)`,
                  color: "#fff", border: "none", padding: "14px 32px",
                  fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 700,
                  letterSpacing: "0.2em", textTransform: "uppercase",
                  cursor: (!userInfo.name || !userInfo.email || infoLoading) ? "not-allowed" : "pointer",
                  borderRadius: 2,
                  opacity: (!userInfo.name || !userInfo.email) ? 0.35 : 1,
                  boxShadow: (!userInfo.name || !userInfo.email || infoLoading) ? "none" : `0 0 28px ${engine.color}44`,
                }}
              >
                <span>{infoLoading ? "⏳" : "⚡"}</span>
                <span>{infoLoading ? "SAVING…" : "SAVE MY RESULTS"}</span>
              </button>
              <p style={{
                marginTop: 10, fontSize: 10,
                color: "#2a3248",
                fontFamily: "'Space Mono', monospace", letterSpacing: "0.08em",
              }}>
                PRIVATE · ENCRYPTED · REVIEWED WITHIN 48H
              </p>
            </div>
          </div>
        ) : (
          <div style={{
            background: "rgba(34,197,94,0.04)", border: "1px solid rgba(34,197,94,0.18)",
            borderRadius: 4, padding: "28px 40px", margin: "0 0 40px",
            display: "flex", alignItems: "center", gap: 16,
          }}>
            <span style={{ fontSize: 22 }}>✅</span>
            <div>
              <div style={{ fontFamily: "'Space Mono',monospace", fontSize: 11, color: "#22c55e", letterSpacing: "0.15em", marginBottom: 4 }}>
                RESULTS SAVED
              </div>
              <div style={{ fontSize: 13, color: "#5a6280" }}>
                We'll review your diagnostic and reach out within 48 hours.
              </div>
            </div>
          </div>
        )}

        {/* Engine Card */}
        <div style={styles.engineCard(engine.cls)}>
          <div style={styles.engineCardTop(engine.color)} />
          <div style={styles.engineNameDisplay(engine.cls)}>{engine.tier} {engine.name}</div>
          <div style={styles.engineTagline}>{engine.tagline}</div>
          <div style={styles.engineDesc}>{engine.desc}</div>
          <div style={styles.gapsTitle}>⚠ Critical System Gaps Identified</div>
          <div style={styles.gapsGrid}>
            {engine.gaps.map(g => (
              <div key={g} style={styles.gapChip(engine.color)}>
                <span style={styles.gapDot(engine.color)} />
                {g}
              </div>
            ))}
          </div>
        </div>

        {/* Engine SVG */}
        <div style={styles.engineViz}>
          <div style={styles.engineVizTitle}>Your Business Engine — Visual Diagnostic</div>
          <div style={{ display: "inline-block", overflowX: "auto", maxWidth: "100%" }}>
            {engine.cls === "eco" && <EcoSVG color={engine.color} />}
            {engine.cls === "v6"  && <V6SVG  color={engine.color} />}
            {engine.cls === "v12" && <V12SVG color={engine.color} />}
          </div>
        </div>

        {/* Breakdown */}
        <div style={styles.breakdownSection}>
          <div style={styles.breakdownTitle}>Score Breakdown by System Category</div>
          <div style={styles.breakdownBars}>
            {sectionScores.map((sec, i) => (
              <BarRow key={sec.label} label={sec.label} score={sec.score} max={sec.max} color={engine.color} delay={600 + i * 80} />
            ))}
          </div>
        </div>

        <div style={{ textAlign: "center" }}>
          <button className="diag-restart" style={styles.restartBtn} onClick={handleRestart}>
            ↺ RETAKE DIAGNOSTIC
          </button>
        </div>
      </div>
    </div>
  );
}